import { Link, Outlet, createFileRoute, useLocation } from '@tanstack/react-router';
import {
  Bell,
  Bot,
  Building2,
  CreditCard,
  Link2,
  MapPin,
  Megaphone,
  MessageSquare,
  Plug,
  Radio,
  Scale,
  Shield,
  ThumbsDown,
  User,
  Users,
} from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';

import { cn } from '@/lib/utils';
import { apiFetch } from '@/lib/query-client';

// Sprint Settings Panel IA — Parte 1. Layout del panel de Configuración.
//
// Estructura:
// - Sidebar interno (240px desktop, scrolleable horizontal en mobile)
//   con 7 items: Mi cuenta, Seguridad, Organización, Equipo, Notificaciones,
//   Billing, Integraciones.
// - Content panel a la derecha, ancho fluido. Outlet renderiza la
//   sub-ruta activa (account/security/etc.).
// - Breadcrumb fijo arriba: "Configuración › [sección]" — visible en
//   ambos desktop y mobile, ayuda a deeplinks compartidos.

export const Route = createFileRoute('/_app/settings')({
  component: SettingsLayout,
});

interface NavItem {
  readonly path: string;
  readonly label: string;
  readonly icon: ReactNode;
  readonly badge?: ReactNode;
}

const NAV_ITEMS: readonly NavItem[] = [
  { path: '/settings/account', label: 'Mi cuenta', icon: <User className="h-4 w-4" /> },
  { path: '/settings/security', label: 'Seguridad', icon: <Shield className="h-4 w-4" /> },
  {
    path: '/settings/organization',
    label: 'Organización',
    icon: <Building2 className="h-4 w-4" />,
  },
  { path: '/settings/team', label: 'Equipo', icon: <Users className="h-4 w-4" /> },
  { path: '/settings/notifications', label: 'Notificaciones', icon: <Bell className="h-4 w-4" /> },
  { path: '/settings/billing', label: 'Billing', icon: <CreditCard className="h-4 w-4" /> },
  { path: '/settings/integrations', label: 'Integraciones', icon: <Plug className="h-4 w-4" /> },
  {
    path: '/settings/labor-params',
    label: 'Parámetros legales',
    icon: <Scale className="h-4 w-4" />,
  },
  { path: '/settings/channels', label: 'Canales', icon: <Radio className="h-4 w-4" /> },
  {
    path: '/settings/whatsapp',
    label: 'WhatsApp',
    icon: <MessageSquare className="h-4 w-4" />,
  },
  { path: '/settings/campaigns', label: 'Campañas', icon: <Megaphone className="h-4 w-4" /> },
  {
    path: '/settings/campaign-links',
    label: 'Links de campaña',
    icon: <Link2 className="h-4 w-4" />,
  },
  {
    path: '/settings/lost-reasons',
    label: 'Motivos pérdida',
    icon: <ThumbsDown className="h-4 w-4" />,
  },
  {
    path: '/settings/ov-sedes',
    label: 'Sedes OV',
    icon: <MapPin className="h-4 w-4" />,
  },
  {
    path: '/settings/camila',
    label: 'Camila',
    icon: <Bot className="h-4 w-4" />,
  },
];

function SettingsLayout() {
  const location = useLocation();
  const camilaStatus = useCamilaStatus();

  // Inject Camila badge into the nav items
  const items = NAV_ITEMS.map((item) =>
    item.path === '/settings/camila' ? { ...item, badge: <CamilaBadge status={camilaStatus} /> } : item,
  );

  const activeItem = items.find((i) => location.pathname.startsWith(i.path)) ?? items[0]!;

  return (
    <div className="flex flex-col gap-4">
      {/* Breadcrumb header — visible always */}
      <header className="flex items-center gap-2 text-sm">
        <span className="text-[color:var(--color-fg-muted)]">Configuración</span>
        <span className="text-[color:var(--color-fg-subtle)]">›</span>
        <span className="font-medium text-[color:var(--color-fg)]">{activeItem.label}</span>
      </header>

      <div className="flex flex-col gap-6 md:flex-row md:gap-8">
        {/* Sidebar interno — desktop: vertical 240px; mobile: tabs horizontales */}
        <nav
          className={cn(
            'shrink-0 md:w-60',
            'flex md:flex-col md:gap-1',
            // Mobile: scroll horizontal con borders
            'overflow-x-auto md:overflow-x-visible',
            'border-b md:border-b-0 md:border-r-0',
            'pb-2 md:pb-0',
          )}
          style={{ borderColor: 'var(--color-border)' }}
          aria-label="Navegación de configuración"
        >
          {items.map((item) => (
            <SettingsNavItem
              key={item.path}
              item={item}
              isActive={location.pathname.startsWith(item.path)}
            />
          ))}
        </nav>

        {/* Content panel */}
        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

interface SettingsNavItemProps {
  readonly item: NavItem;
  readonly isActive: boolean;
}

function SettingsNavItem({ item, isActive }: SettingsNavItemProps) {
  return (
    <Link
      to={item.path}
      className={cn(
        'group flex shrink-0 items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors',
        // Mobile: items inline; desktop: vertical
        'whitespace-nowrap',
        isActive
          ? 'bg-[color:var(--color-bg-overlay)] font-medium text-[color:var(--color-fg)]'
          : 'text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-overlay)] hover:text-[color:var(--color-fg)]',
      )}
    >
      <span className="shrink-0">{item.icon}</span>
      <span className="flex-1">{item.label}</span>
      {item.badge}
    </Link>
  );
}

// ── Camila status badge ──────────────────────────────────────────────

interface CamilaEffective {
  readonly enabled: boolean;
  readonly channels: string[];
}

const CHANNEL_SHORT: Record<string, string> = {
  whatsapp: 'WA',
  instagram: 'IG',
  messenger: 'FB',
};

function useCamilaStatus(): CamilaEffective | null {
  const [status, setStatus] = useState<CamilaEffective | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ effective: CamilaEffective }>({
      path: '/v1/feature-flags/camila',
      method: 'GET',
    })
      .then((res) => {
        if (!cancelled) setStatus(res.effective);
      })
      .catch(() => {
        // Silently ignore — badge just won't show
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return status;
}

function CamilaBadge({ status }: { readonly status: CamilaEffective | null }) {
  if (!status) return null;

  if (!status.enabled) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-current" />
        Off
      </span>
    );
  }

  const labels = status.channels.map((ch) => CHANNEL_SHORT[ch] ?? ch).join(' ');

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-current" />
      {labels}
    </span>
  );
}
